import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import useRedirect, { REDIRECT_QUERY } from "@/hooks/useRedirect";
import { useAuthStore } from "@/lib/auth/AuthStore";
import { mixpanelEvent } from "@/lib/mixpanels/mixpanel";
import { CASH_INDEX_PAGE, LIBRARIES_INDEX_PAGE, LOGIN_PAGE, MYPAGE_PAGE, PASSES_LANDING, REGISTRATION_PAGE, SOLVOOK_WRITE_STUDIO } from "@/settings/constant";
import { Divider, IconV2, Button as SDSButton, Typography } from "@bookips/sds";
import { useQueryClient } from "@tanstack/react-query";
import Link from "next/link";
import { useRouter } from "next/router";
import { useHomeSwitchStore } from "src/store/homeSwitch.store";
import { useTooltip } from "../common/GNB/useTooltip";
import Icon from "../icon/Icon";
import { ArrowLeft, Cash, Close1_5px, Pass, Sizes } from "../icon/IconList";
import { QUERY_KEYS } from "../myPage/constants/queryKeys";
import { useSideCategoryMenuStore } from "./store/useCategoryMenuStore";
export const SideSheetHeader = (param)=>{
    let { title , onClose , goToPrevStep , isShowCloseButton =true  } = param;
    return /*#__PURE__*/ _jsxs("div", {
        className: "box-border flex h-14 items-center justify-between border-b border-gray-100 px-5",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-2",
                children: [
                    !!goToPrevStep && /*#__PURE__*/ _jsx(Icon, {
                        onClick: goToPrevStep,
                        icon: ArrowLeft,
                        size: Sizes.xmedium,
                        className: "cursor-pointer text-gray-900"
                    }),
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "subtitle1",
                        className: "text-gray-900",
                        children: title
                    })
                ]
            }),
            isShowCloseButton && /*#__PURE__*/ _jsx(Icon, {
                onClick: onClose,
                icon: Close1_5px,
                size: Sizes.xmedium,
                className: "cursor-pointer text-gray-900"
            })
        ]
    });
};
export const SideSheetUtils = (param)=>{
    let { goToNextStep  } = param;
    const router = useRouter();
    const currentPathname = router.asPath;
    const { isLoggedIn , user  } = useAuthStore();
    const { redirectToLogin  } = useRedirect();
    const { handleTooltip  } = useTooltip({
        cookieName: "expertTooltip",
        day: 1
    });
    const homeType = useHomeSwitchStore((state)=>state.homeType);
    const queryClient = useQueryClient();
    const closeSideMenu = useSideCategoryMenuStore((state)=>state.closeSideMenu);
    const trackClick = (type, name, url)=>{
        mixpanelEvent("Side Sheets Button Clicked", {
            "Button Type": type,
            "Button Name": name,
            "Button Url": url
        });
    };
    const loginMenuItems = [
        {
            title: "엑스퍼트",
            icon: IconV2.Expert,
            link: SOLVOOK_WRITE_STUDIO,
            type: "expert"
        },
        {
            title: "자료 보관함",
            icon: IconV2.OldLibrary,
            link: LIBRARIES_INDEX_PAGE,
            type: "library"
        },
        {
            title: "마이 쏠북",
            icon: IconV2.User,
            link: MYPAGE_PAGE,
            type: "mypage"
        }
    ];
    const handleLoginMenuClick = (e, menu)=>{
        trackClick(menu.title, menu.type, menu.link);
        if (menu.type === "expert") handleTooltip();
        if (menu.type === "mypage") {
            e.preventDefault();
            mixpanelEvent("Side Sheet Viewed");
            closeSideMenu();
            // 이미 마이 쏠북 페이지면 데이터 리프레시
            if (router.pathname === MYPAGE_PAGE) {
                queryClient.invalidateQueries({
                    queryKey: [
                        ...QUERY_KEYS.MY_SOLVOOK_HOME
                    ]
                });
                window.scrollTo(0, 0);
                return;
            }
            router.push(MYPAGE_PAGE).then(()=>window.scrollTo(0, 0));
        }
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-col gap-3 p-5 pt-6",
        children: [
            isLoggedIn ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsxs("p", {
                        className: "text-center text-xl leading-[30px] font-bold text-gray-900",
                        children: [
                            (user === null || user === void 0 ? void 0 : user.displayName) || "회원",
                            "님 환영해요!"
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex w-full rounded-md border border-gray-100 py-[7px]",
                        children: loginMenuItems.map((menu, index)=>/*#__PURE__*/ _jsxs("div", {
                                className: "flex flex-1 items-center justify-center",
                                children: [
                                    /*#__PURE__*/ _jsxs(Link, {
                                        href: menu.link,
                                        onClick: (e)=>handleLoginMenuClick(e, menu),
                                        className: "text-primary flex h-[52px] flex-1 flex-col items-center justify-center gap-0.5 text-center hover:text-inherit",
                                        children: [
                                            /*#__PURE__*/ _jsx(menu.icon, {}),
                                            /*#__PURE__*/ _jsx(Typography, {
                                                variant: "label",
                                                children: menu.title
                                            })
                                        ]
                                    }),
                                    index < loginMenuItems.length - 1 && /*#__PURE__*/ _jsx(Divider, {
                                        orientation: "vertical",
                                        className: "h-6 shrink-0 self-center"
                                    })
                                ]
                            }, menu.type))
                    })
                ]
            }) : /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-col gap-2",
                children: [
                    /*#__PURE__*/ _jsx(SDSButton, {
                        size: "lg",
                        variant: "stroke",
                        color: "secondary",
                        onClick: ()=>{
                            trackClick("로그인", "login", LOGIN_PAGE);
                            redirectToLogin();
                        },
                        children: "로그인"
                    }),
                    /*#__PURE__*/ _jsx(SDSButton, {
                        size: "lg",
                        variant: "fill",
                        color: "secondary",
                        onClick: ()=>{
                            trackClick("회원가입", "signup", REGISTRATION_PAGE);
                            router.push({
                                pathname: REGISTRATION_PAGE,
                                query: currentPathname.length > 1 && {
                                    [REDIRECT_QUERY]: currentPathname
                                }
                            });
                        },
                        children: "회원가입"
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex w-full items-center",
                children: [
                    /*#__PURE__*/ _jsxs(Link, {
                        href: "".concat(PASSES_LANDING, "/").concat(homeType),
                        onClick: ()=>trackClick("이용권 구매", "이용권 구매", "".concat(PASSES_LANDING, "/").concat(homeType)),
                        className: "flex w-full items-center justify-center gap-2 px-4 py-2.5",
                        children: [
                            /*#__PURE__*/ _jsx(Pass, {}),
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "subtitle2",
                                className: "text-primary-main",
                                children: "이용권 구매"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(Divider, {
                        orientation: "vertical",
                        className: "h-4 shrink-0 self-center"
                    }),
                    /*#__PURE__*/ _jsxs(Link, {
                        href: CASH_INDEX_PAGE,
                        onClick: ()=>trackClick("캐시 충전", "캐시 충전", CASH_INDEX_PAGE),
                        className: "flex w-full items-center justify-center gap-2 px-4 py-2.5",
                        children: [
                            /*#__PURE__*/ _jsx(Icon, {
                                icon: Cash,
                                size: Sizes.small
                            }),
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "subtitle2",
                                className: "text-primary-main",
                                children: "캐시 충전"
                            })
                        ]
                    })
                ]
            })
        ]
    });
};
